<template>
  <div>
    <div v-if="!hasElectionRanking" class="votelist-header">
      <span>
        Vote(s) remaining:
        <strong>{{ votesLeft }}</strong>
      </span>
    </div>

    <!-- Toggle List -->
    <div class="toggle-list mdl-shadow--2dp">
      <div v-for="(candidate, index) in nominees" :key="candidate" v-cloak>
        <toggle-list-item
          :value="candidate"
          :icon="type !== 'simple-vote' ? 'account_circle' : ''"
          toggle-icon="close"
          :index="index"
          :index-choice="voteList.indexOf(candidate)"
          :text="candidate"
          :custom-options="customOptions"
          :checked-status="voteList.indexOf(candidate) >= 0 ? true : false"
          :disabled-status="
            voteList.indexOf(candidate) < 0 && votesLeft === 0 ? true : false
          "
          toggle-class="toggle-content-action"
          @inputting="updateSelection"
        />
      </div>
    </div>

    <div class="add-candidate-container" v-if="!isHideWriteInCandidate">
      <mdl-textfield
        field-id="add-candidate"
        label="Don't see your nominee? Add them here."
        placeholder="eg. John Smith"
        :show-chars-limit="true"
        :is-disabled="additionalVotesLeft <= 0"
        checked-status="false"
        @inputting="updateNewCandidateName"
        @enter-pressed="addToNomineeList"
        :bvalue="newCandidateName"
      />

      <!-- Add vote from textfield button -->
      <button
        type="button"
        class="mdl-button mdl-js-button mdl-button--raised mdl-button--colored"
        @click="addToNomineeList"
        :disabled="
          isInvalidName || additionalVotesLeft <= 0 || isNewCandidateNameBlank
        "
      >
        Add
      </button>
    </div>

    <!-- Error messaging -->
    <div class="error-messages">
      <div
        v-show="isInvalidName && !isNewCandidateNameBlank"
        transition="slide-fade"
      >
        <div class="info">
          <i>Nominee name already exists</i>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';

import ToggleListItem from '@/components/Questions/ToggleListItem.vue';
import mdlTextfield from '@/components/mdlTextfield.vue';

import {mapActions, mapState, mapGetters} from 'vuex';
import {sanitizeCandidate} from '@/vuex/helpers.js';

export default {
  props: {
    hasCandidates: {
      type: Boolean,
      required: true
    },
    votes: {
      type: Array
    },
    nominees: {
      type: Array
    },
    questionId: {
      type: [String, Number],
      required: true
    },
    votesLeft: {
      type: Number,
      required: true
    },
    customOptions: {
      type: Object
    },
    hasElectionRanking: {
      type: Boolean,
      default: false
    },
    type: {
      type: String
    }
  },
  components: {
    ToggleListItem,
    mdlTextfield
  },
  computed: {
    ...mapState('candidates', ['additionalVotesNum']),
    ...mapGetters('candidates', ['additionalVoteById']),
    isNewCandidateNameBlank() {
      const formatted = _.startCase(
        _.toLower(sanitizeCandidate(this.newCandidateName))
      );

      return this.newCandidateName.trim() === '' || formatted.length === 0;
    },
    isInvalidName() {
      if (!this.nomineeList) {
        return false;
      }

      return !this.isValidCandidate(this.newCandidateName);
    },
    isHideWriteInCandidate() {
      if (this.type === 'simple-vote') return true;
      if (!this.customOptions) {
        return false;
      }
      return (
        this.customOptions.hideWriteInCandidate ||
        this.customOptions.hide_write_in_candidate
      );
    }
  },
  data() {
    return {
      newCandidateName: '',
      voteList: this.votes,
      nomineeList: this.nominees,
      additionalVotesLeft: 0
    };
  },
  methods: {
    ...mapActions('questions', ['updateVote']),
    ...mapActions('candidates', ['addCandidate']),

    // Updates the vote selection in Vuex
    updateSelection(candidate) {
      let index = this.voteList.indexOf(candidate);
      let isVoteCast = true;

      if (index >= 0) {
        isVoteCast = false;
        this.voteList.splice(index, 1);
      } else {
        isVoteCast = true;
        this.voteList.push(candidate);
      }

      this.updateVotesLeft(isVoteCast);

      // If array is empty, insert empty string in array
      // Required for API to interpret abstain vote
      if (this.voteList.length === 0) {
        this.updateVote({
          questionId: this.questionId,
          vote: ['']
        });
      } else {
        this.updateVote({
          questionId: this.questionId,
          vote: this.voteList
        });
      }
    },

    addToNomineeList() {
      // Only add to nominee list if passes validation
      if (!this.isValidCandidate(this.newCandidateName)) {
        return;
      }

      // Format candidate name
      const formatted = _.startCase(
        _.toLower(sanitizeCandidate(this.newCandidateName))
      );

      if (formatted === '') {
        return;
      }

      // Push to both lists
      this.voteList.push(formatted);
      this.nomineeList.push(formatted);

      // Push to additional candidate list
      this.addCandidate({questionId: this.questionId, candidate: formatted});
      this.updateVotesLeft(true);

      this.updateVote({
        questionId: this.questionId,
        vote: this.voteList
      });

      // Clear the text fields
      this.newCandidateName = '';
    },
    updateNewCandidateName(name) {
      this.newCandidateName = name;
    },
    isValidCandidate(candidate) {
      // Valid candidate should have a name and should not already be in the
      // list of nominee options.

      // Ensure blank spaces cannot be entered as options
      if (candidate.trim() === '') {
        return false;
      }

      return (
        this.nomineeList
          .map(nominee => {
            return sanitizeCandidate(nominee);
          })
          .indexOf(sanitizeCandidate(this.newCandidateName)) < 0
      );
    },
    setAdditionalVotesLeft() {
      // If ranking is on then from additional write in amount
      // substract how many additional votes have already been cast
      if (this.hasElectionRanking) {
        this.additionalVotesLeft = this.voteCount();
      }
      // If ranking is off then determine if write-in amount isn't higher
      // than allowed amount
      else {
        this.additionalVotesLeft = this.voteCount() > 0 ? this.votesLeft : 0;
      }
    },
    updateVotesLeft(isAdded) {
      if (this.hasElectionRanking) {
        this.additionalVotesLeft = this.voteCount();
      } else {
        if (isAdded && this.additionalVotesLeft > 0) {
          --this.additionalVotesLeft;
        } else if (!isAdded && this.voteCount() > 0) {
          ++this.additionalVotesLeft;
        }
      }
    },
    voteCount() {
      const candidateAmount = this.additionalVoteById(this.questionId)
        ? this.additionalVoteById(this.questionId).length
        : 0;
      return Number(this.additionalVotesNum - candidateAmount);
    }
  },
  mounted() {
    this.setAdditionalVotesLeft();

    this.$nextTick(() => {
      componentHandler.upgradeAllRegistered();
    });
  }
};
</script>

<style scoped>
.votelist-header {
  text-align: left;
  color: rgba(63, 81, 181, 0.92);
  margin: 0px 0px 8px 0px;
  font-size: 12px;
}
.toggle-list {
  border-radius: 4px;
  margin-bottom: 2rem;
  background-color: #ffffff;
}
.add-candidate-container {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
}
/* Aligns add button with textfield */
.add-candidate-container button {
  margin-top: 10px;
}
</style>



// WEBPACK FOOTER //
// src/components/Questions/Elections/ElectionsVoteListWithCandidates.vue